import type { ComponentsDefinition } from '@eneco/cms';
import { cmsComponents } from '@eneco/cms-components';
import dynamic from 'next/dynamic';

/**
 * The CMS-agnostic registry for this container: the shared one plus what only this
 * container mounts.
 *
 * Applications are registered per container on the Sitecore lane too, in
 * `sitecoreComponents.ts`, and for the same reason: they cannot live in the shared
 * registry. `lib:cms-components` may not depend on `app:next` (see `depConstraints`
 * in `eslint.config.mjs`), and every container passes that one map, so a flow put
 * there would also be pulled into the containers that never render it.
 *
 * The cookie wall is one of those applications. It sits in the page's `jss-meta`
 * placeholder, which `CmsLayout` renders on this lane too, and reads its content
 * through `@eneco/cms`, so the same component serves both lanes. The chat sits there too; its CMS-lane entry
 * is `ChatInner`, which reads its content through `@eneco/cms`.
 *
 * The registration flow is registered under both its rendering names as its CMS-lane entry
 * (`RegistrationFlowInner`), which picks the variant from the rendering's name.
 *
 * `AffiliateCalculationTool` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * `AdvancedCalculationTool` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * `Unsubscribe` is registered for the same reason, and reads its content
 * through `@eneco/cms` too.
 *
 * So is the heat comparison tool, registered as its inner component: this lane's
 * `Placeholder` already hands it its rendering, which the Sitecore lane's bridge builds itself.
 * The inspiration advice filter and the decarbonization potential breakdown are registered the same way.
 *
 * `AppPlaceholder` looks the key up by the name after `jss-app-`, so it has to match
 * the placeholder on the page, and it matches the Sitecore rendering name one to one.
 *
 * Without this file the two lanes are not comparable on this container: the flow is
 * registered on the Sitecore lane here (`sitecoreComponents.ts`), so a route renders
 * by default and renders empty under `?_cms=sanity`, which reads as a content problem
 * when it is only a missing registration.
 *
 * A flow is added here once its steps read their content through `@eneco/cms`. Until
 * then it stays out: its steps still read Sitecore directly, so on this lane it would
 * fail at the first step rather than render.
 */
const components: ComponentsDefinition = {
  ...cmsComponents,
  AdvancedCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/AdvancedCalculationTool')),
  AffiliateCalculationTool: dynamic(() => import('@apps/flows-nl-eneco-energyFlow/CalculationTool')),
  'bundle-flow': dynamic(() => import('@apps/flows-nl-eneco-bundleFlow/BundleFlow')),
  Chat: dynamic(() => import('@apps/chat-shared-multilabel/Chat').then(mod => mod.ChatInner)),
  'servicegemak-flow': dynamic(() => import('@apps/flows-nl-eneco-serviceGemakFlow/ServiceGemakFlow')),
  'contract-extension-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/ContractExtensionFlow')),
  'contract-advice-flow': dynamic(() => import('@apps/flows-nl-eneco-contractAdviceFlow/ContractAdviceFlow')),
  CookieWall: dynamic(() => import('@apps/cookiewall-nl-multilabel/CookieWall')),
  CustomerReview: dynamic(() =>
    import('@apps/customerreview-shared-multilabel/CustomerReview').then(mod => mod.CustomerReviewInner),
  ),
  'b2b-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-b2bLeadFlow/B2BLeadFlow')),
  'cv-ketel-koop-flow': dynamic(() => import('@apps/flows-nl-eneco-cvKetelKoopFlow/CvKetelKoopFlow')),
  'dongle-flow': dynamic(() => import('@apps/flows-nl-eneco-dongleFlow/DongleFlow')),
  'decarbonization-lead-flow': dynamic(
    () => import('@apps/flows-nl-eneco-decarbonisationLeadFlow/DecarbonisationLeadFlow'),
  ),
  DecarbonizationPotentialBreakdown: dynamic(() =>
    import('@apps/decarbonizationPotentialBreakdown-nl-eneco/DecarbonizationPotentialBreakdown').then(
      mod => mod.DecarbonizationPotentialBreakdownInner,
    ),
  ),
  'energy-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/EnergyFlow')),
  FlowThankYouHero: dynamic(() => import('@apps/flowthankyouhero-nl-eneco/FlowThankYouHeroInner')),
  'heat-pump-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-heatPumpLeadFlow/HeatPumpLeadFlow')),
  FlowThankYouSummary: dynamic(() => import('@apps/flowthankyousummary-nl-eneco/FlowThankYouSummaryInner')),
  FlowThankYouSteps: dynamic(() => import('@apps/flowthankyousteps-nl-eneco/FlowThankYouStepsInner')),
  HeatComparisonTool: dynamic(() =>
    import('@apps/calculator-nl-eneco/HeatComparisonCalculator').then(mod => mod.HeatComparisonCalculatorInner),
  ),
  InspirationAdviceFilter: dynamic(() =>
    import('@apps/inspirationadvicefilter-nl-eneco/InspirationAdviceFilter').then(
      mod => mod.InspirationAdviceFilterInner,
    ),
  ),
  'solar-boiler-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-solarBoilerLeadFlow/SolarBoilerLeadFlow')),
  'legislation-compass-flow': dynamic(
    () => import('@apps/flows-nl-eneco-legislationCompassFlow/LegislationCompassFlow'),
  ),
  'dongle-cancelation-flow': dynamic(() => import('@apps/flows-nl-eneco-dongleCancelationFlow/DongleCancelationFlow')),
  'deceased-flow': dynamic(() => import('@apps/flows-nl-eneco-deceasedCancelationFlow/DeceasedCancelationFlow')),
  'ketelcomfort-cancelation-flow': dynamic(
    () => import('@apps/flows-nl-eneco-ketelComfortCancelationFlow/KetelComfortCancelationFlow'),
  ),
  'thermostat-cancellation-flow': dynamic(
    () => import('@apps/flows-nl-eneco-thermostatCancellationFlow/ThermostatCancellationFlow'),
  ),
  RegistrationAccountCreate: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
  RegistrationAccountReset: dynamic(() =>
    import('@apps/accessmanagement-nl-multilabel/components/RegistrationFlow/RegistrationFlow').then(
      mod => mod.RegistrationFlowInner,
    ),
  ),
  'sme-energy-flow-v2': dynamic(() => import('@apps/flows-nl-eneco-smeEnergyFlow/SmeEnergyFlow')),
  'thermostat-flow': dynamic(() => import('@apps/flows-nl-eneco-thermostatFlow/ThermostatFlow')),
  'toon-cancelation-flow': dynamic(() => import('@apps/flows-nl-eneco-toonCancelationFlow/ToonCancelationFlow')),
  'toon-subscription-flow': dynamic(() => import('@apps/flows-nl-eneco-toonFlow/ToonSubscriptionFlow')),
  'home-battery-lead-flow': dynamic(() => import('@apps/flows-nl-eneco-homeBatteryLeadFlow/HomeBatteryLeadFlow')),
  Unsubscribe: dynamic(() => import('@apps/unsubscribe-shared-multilabel/Unsubscribe')),
  'urgent-energy-flow': dynamic(() => import('@apps/flows-nl-eneco-energyFlow/UrgentEnergyFlow')),
};

export default components;
